// src/services/api/reportesApi.ts - VERSIÓN COMPLETA ACTUALIZADA
import api from '../../utils/axios';
import {
  FiltroReporte,
  DashboardKPI,
  EstadisticaVentas,
  ReporteVentasDetalle,
  ReporteVentasPorMes,
  TopProducto,
  ProductoRentabilidad,
  ReporteComprasPorProveedor,
  EstadoArchivado,
  ResultadoArchivado,
  ReporteImpuestos,
  ReporteInventario,
  ReporteDevoluciones,
  ReporteClientes,
  ReporteVendedores,
  AnalisisABC,
  ComparativaPeriodos,
  ReporteAlertas,
  ReporteAuditoria,
} from '../../components/types/reportes.types';

export class ReportesApi {
  // ==========================================
  // DASHBOARD
  // ==========================================
  
  static async obtenerDashboard(filtros?: {
    mesActual?: boolean;
    idSucursal?: number;
  }): Promise<DashboardKPI> {
    try {
      const params = new URLSearchParams();
      if (filtros?.mesActual !== undefined) {
        params.append('mesActual', filtros.mesActual.toString());
      }
      if (filtros?.idSucursal) {
        params.append('idSucursal', filtros.idSucursal.toString());
      }

      const response = await api.get<DashboardKPI>(
        `/reportes/dashboard?${params.toString()}`
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error, 'Error al obtener dashboard');
    }
  }

  static async obtenerEstadisticasVentas(
    filtros: FiltroReporte
  ): Promise<EstadisticaVentas[]> {
    try {
      const params = this.construirParams(filtros);
      const response = await api.get<EstadisticaVentas[]>(
        `/reportes/estadisticas/ventas?${params.toString()}`
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error, 'Error al obtener estadísticas');
    }
  }

  // ==========================================
  // VENTAS
  // ==========================================

  static async obtenerReporteVentas(
    filtros: FiltroReporte
  ): Promise<ReporteVentasDetalle[]> {
    try {
      const params = this.construirParams(filtros);
      const response = await api.get<ReporteVentasDetalle[]>(
        `/reportes/ventas?${params.toString()}`
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error, 'Error al obtener reporte de ventas');
    }
  }

  static async obtenerVentasPorMes(filtros: {
    ano: number;
    mes?: number;
    idSucursal?: number;
  }): Promise<ReporteVentasPorMes[]> {
    try {
      const params = new URLSearchParams();
      params.append('ano', filtros.ano.toString());
      if (filtros.mes) params.append('mes', filtros.mes.toString());
      if (filtros.idSucursal) params.append('idSucursal', filtros.idSucursal.toString());

      const response = await api.get<ReporteVentasPorMes[]>(
        `/reportes/ventas/por-mes?${params.toString()}`
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error, 'Error al obtener ventas por mes');
    }
  }

  // ==========================================
  // PRODUCTOS
  // ==========================================

  static async obtenerTopProductos(filtros: FiltroReporte): Promise<TopProducto[]> {
    try {
      const params = this.construirParams(filtros);
      const response = await api.get<TopProducto[]>(
        `/reportes/productos/top?${params.toString()}`
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error, 'Error al obtener top productos');
    }
  }

  static async obtenerRentabilidad(
    filtros: FiltroReporte
  ): Promise<ProductoRentabilidad[]> {
    try {
      const params = this.construirParams(filtros);
      const response = await api.get<ProductoRentabilidad[]>(
        `/reportes/productos/rentabilidad?${params.toString()}`
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error, 'Error al obtener rentabilidad');
    }
  }

  static async obtenerAnalisisABC(
    filtros: FiltroReporte
  ): Promise<AnalisisABC[]> {
    try {
      const params = this.construirParams(filtros);
      const response = await api.get<AnalisisABC[]>(
        `/reportes/productos/abc?${params.toString()}`
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error, 'Error al obtener análisis ABC');
    }
  }

  // ==========================================
  // COMPRAS
  // ==========================================

  static async obtenerComprasPorProveedor(
    filtros: FiltroReporte
  ): Promise<ReporteComprasPorProveedor[]> {
    try {
      const params = this.construirParams(filtros);
      const response = await api.get<ReporteComprasPorProveedor[]>(
        `/reportes/compras/por-proveedor?${params.toString()}`
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error, 'Error al obtener compras por proveedor');
    }
  }

  // ==========================================
  // IMPUESTOS
  // ==========================================

  static async obtenerReporteImpuestos(
    filtros: FiltroReporte
  ): Promise<ReporteImpuestos[]> {
    try {
      const params = this.construirParams(filtros);
      const response = await api.get<ReporteImpuestos[]>(
        `/reportes/impuestos?${params.toString()}`
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error, 'Error al obtener reporte de impuestos');
    }
  }

  // ==========================================
  // INVENTARIO
  // ==========================================

  static async obtenerReporteInventario(
    filtros: FiltroReporte
  ): Promise<ReporteInventario[]> {
    try {
      const params = this.construirParams(filtros);
      const response = await api.get<ReporteInventario[]>(
        `/reportes/inventario?${params.toString()}`
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error, 'Error al obtener reporte de inventario');
    }
  }

  // ==========================================
  // DEVOLUCIONES
  // ==========================================

  static async obtenerDevolucionesVentas(
    filtros: FiltroReporte
  ): Promise<ReporteDevoluciones[]> {
    try {
      const params = this.construirParams(filtros);
      const response = await api.get<ReporteDevoluciones[]>(
        `/reportes/devoluciones/ventas?${params.toString()}`
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error, 'Error al obtener devoluciones de ventas');
    }
  }

  static async obtenerDevolucionesCompras(
    filtros: FiltroReporte
  ): Promise<ReporteDevoluciones[]> {
    try {
      const params = this.construirParams(filtros);
      const response = await api.get<ReporteDevoluciones[]>(
        `/reportes/devoluciones/compras?${params.toString()}`
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error, 'Error al obtener devoluciones de compras');
    }
  }

  // ==========================================
  // CLIENTES Y VENDEDORES
  // ==========================================

  static async obtenerReporteClientes(
    filtros: FiltroReporte
  ): Promise<ReporteClientes[]> {
    try {
      const params = this.construirParams(filtros);
      const response = await api.get<ReporteClientes[]>(
        `/reportes/clientes?${params.toString()}`
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error, 'Error al obtener reporte de clientes');
    }
  }

  static async obtenerReporteVendedores(
    filtros: FiltroReporte
  ): Promise<ReporteVendedores[]> {
    try {
      const params = this.construirParams(filtros);
      const response = await api.get<ReporteVendedores[]>(
        `/reportes/vendedores?${params.toString()}`
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error, 'Error al obtener reporte de vendedores');
    }
  }

  // ==========================================
  // ANÁLISIS AVANZADOS
  // ==========================================

  static async obtenerComparativaPeriodos(
    periodo1Inicio: string,
    periodo1Fin: string,
    periodo2Inicio: string,
    periodo2Fin: string
  ): Promise<ComparativaPeriodos[]> {
    try {
      const params = new URLSearchParams({
        periodo1Inicio,
        periodo1Fin,
        periodo2Inicio,
        periodo2Fin,
      });
      const response = await api.get<ComparativaPeriodos[]>(
        `/reportes/analisis/comparativa?${params.toString()}`
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error, 'Error al obtener comparativa de períodos');
    }
  }

  // ==========================================
  // ALERTAS Y AUDITORÍA
  // ==========================================

  static async obtenerReporteAlertas(): Promise<ReporteAlertas[]> {
    try {
      const response = await api.get<ReporteAlertas[]>('/reportes/alertas');
      return response.data;
    } catch (error) {
      throw this.handleError(error, 'Error al obtener alertas');
    }
  }

  static async obtenerReporteAuditoria(
    filtros: FiltroReporte
  ): Promise<ReporteAuditoria[]> {
    try {
      const params = this.construirParams(filtros);
      const response = await api.get<ReporteAuditoria[]>(
        `/reportes/auditoria?${params.toString()}`
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error, 'Error al obtener reporte de auditoría');
    }
  }

  // ==========================================
  // ARCHIVADO
  // ==========================================

  static async obtenerEstadoArchivado(): Promise<EstadoArchivado> {
    try {
      const response = await api.get<EstadoArchivado>('/reportes/archivado/estado');
      return response.data;
    } catch (error) {
      throw this.handleError(error, 'Error al obtener estado de archivado');
    }
  }

  static async ejecutarArchivado(forzar: boolean = false): Promise<ResultadoArchivado> {
    try {
      const params = new URLSearchParams();
      if (forzar) params.append('forzar', 'true');

      const response = await api.post<ResultadoArchivado>(
        `/reportes/archivado/ejecutar?${params.toString()}`
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error, 'Error al ejecutar archivado');
    }
  }

  // ==========================================
  // UTILIDADES
  // ==========================================

  private static construirParams(filtros: FiltroReporte): URLSearchParams {
    const params = new URLSearchParams();

    if (filtros.fechaInicio) params.append('fechaInicio', filtros.fechaInicio);
    if (filtros.fechaFin) params.append('fechaFin', filtros.fechaFin);
    if (filtros.ano) params.append('ano', filtros.ano.toString());
    if (filtros.mes) params.append('mes', filtros.mes.toString());
    if (filtros.idSucursal) params.append('idSucursal', filtros.idSucursal.toString());
    if (filtros.idUsuario) params.append('idUsuario', filtros.idUsuario.toString());
    if (filtros.idCliente) params.append('idCliente', filtros.idCliente.toString());
    if (filtros.idProveedor) params.append('idProveedor', filtros.idProveedor.toString());
    if (filtros.idCategoria) params.append('idCategoria', filtros.idCategoria.toString());
    if (filtros.incluirHistorico !== undefined) {
      params.append('incluirHistorico', filtros.incluirHistorico.toString());
    }
    if (filtros.limite) params.append('limite', filtros.limite.toString());
    if (filtros.soloStockBajo) params.append('soloStockBajo', 'true');
    if (filtros.soloErrores) params.append('soloErrores', 'true');
    if (filtros.modulo) params.append('modulo', filtros.modulo);

    return params;
  }

  static obtenerFechasPorPeriodo(periodo: string): {
    fechaInicio: string;
    fechaFin: string;
  } {
    const hoy = new Date();
    let fechaInicio: Date;
    let fechaFin = new Date(hoy);

    switch (periodo) {
      case 'hoy':
        fechaInicio = new Date(hoy);
        break;
      case 'ayer':
        fechaInicio = new Date(hoy);
        fechaInicio.setDate(fechaInicio.getDate() - 1);
        fechaFin = new Date(fechaInicio);
        break;
      case 'semana':
        fechaInicio = new Date(hoy);
        fechaInicio.setDate(fechaInicio.getDate() - 7);
        break;
      case 'mes':
        fechaInicio = new Date(hoy.getFullYear(), hoy.getMonth(), 1);
        break;
      case 'trimestre':
        const mesActual = hoy.getMonth();
        const inicioTrimestre = Math.floor(mesActual / 3) * 3;
        fechaInicio = new Date(hoy.getFullYear(), inicioTrimestre, 1);
        break;
      case 'ano':
        fechaInicio = new Date(hoy.getFullYear(), 0, 1);
        break;
      default:
        fechaInicio = new Date(hoy.getFullYear(), hoy.getMonth(), 1);
    }

    return {
      fechaInicio: fechaInicio.toISOString().split('T')[0],
      fechaFin: fechaFin.toISOString().split('T')[0],
    };
  }

  static formatearMoneda(valor: number): string {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0,
    }).format(valor);
  }

  static formatearPorcentaje(valor: number): string {
    return `${valor.toFixed(2)}%`;
  }

  static formatearFecha(fecha: string): string {
    return new Date(fecha).toLocaleDateString('es-CO', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  }

  private static handleError(error: any, mensaje: string): Error {
    if (error.response) {
      throw new Error(error.response.data.message || mensaje);
    }
    throw new Error(mensaje);
  }
}